import { Box, Button, Grid, IconButton, Typography } from '@mui/material';
import BaseDialog from 'common/components/base-dialog';
import {
    colors,
    urlExportQuestionTemplate,
    urlGuideUpload,
} from 'common/constants';
import React, { useEffect } from 'react';
import { createStyleSheet, handleError } from 'utils/helpers';
import IosShareOutlinedIcon from '@mui/icons-material/IosShareOutlined';
import UploadFileArea from './upload-file-area';
import FormProvider from 'common/components/form-provider';
import { useForm } from 'react-hook-form';
import { uploadFileQuestionSchema } from 'common/schema';
import { yupResolver } from '@hookform/resolvers/yup';
import { common } from '@mui/material/colors';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { useAppDispatch } from 'common/hook';
import { setIsLoading } from 'slices/loadingSlice';
import {
    downloadFileError,
    editCampaign,
    importQuestionExcel,
} from 'services/campaign.service';
import { IBodyResponse } from 'types/type.global';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { ReactComponent as SaveIcon } from 'assets/images/icons/question-design/save-question.svg';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

interface IModalUplooadFileQuestionProps {
    open: boolean;
    onClose: () => void;
    getListQuestion: () => void;
}
const ModalUplooadFileQuestion: React.FC<IModalUplooadFileQuestionProps> = ({
    open,
    onClose,
    getListQuestion,
}) => {
    const methods = useForm({
        resolver: yupResolver(uploadFileQuestionSchema),
        defaultValues: {
            file: undefined,
        },
        mode: 'onChange',
    });
    const {
        handleSubmit,
        watch,
        resetField,
        trigger,
        setError,
        formState: { errors },
    } = methods;

    const [uploadResponse, setUploadResponse] =
        React.useState<IBodyResponse<null>>();
    const [errorFileUpload, setErrorFileUpload] = React.useState<string>('');

    const campaignId = useParams<{ id: string }>().id;

    const onSubmit = handleSubmit(async (data) => {
        const formData = new FormData();
        formData.append('survey[file]', data.file as File);
        try {
            dispatch(setIsLoading(true));
            const isValid = await trigger('file');
            if (!isValid) {
                return;
            }
            const response = await editCampaign(formData, Number(campaignId));
            if (response.success) {
                toast.success(response.message);
                onClose();
                getListQuestion();
            } else {
                toast.error(response.message);
                Object.entries(response.message).forEach(([, value]) => {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    setError('file', {
                        type: 'manual',
                        message: value,
                    });
                });
            }
        } catch (error) {
            handleError(error);
        } finally {
            dispatch(setIsLoading(false));
        }
    });
    const file = watch('file');

    const dispatch = useAppDispatch();

    const renderFileName = () => {
        if (file instanceof File) {
            return file.name;
        }
        return 'No file selected';
    };

    useEffect(() => {
        const uploadFileQuestionExcel = async () => {
            if (!file) return;
            const isValid = await trigger('file');
            if (!isValid) return;
            dispatch(setIsLoading(true));
            const formData = new FormData();
            formData.append('survey[file]', file as File);
            try {
                const response = await importQuestionExcel(formData);
                if (response.success) {
                    setErrorFileUpload(response.message);
                    setUploadResponse(response);
                    dispatch(setIsLoading(false));
                } else {
                    const newError = {
                        message: '',
                        data: response.data,
                        status_code: response.status_code,
                        success: response.success,
                    };
                    setUploadResponse(newError);
                    Object.entries(response.message).forEach(([key, value]) => {
                        setErrorFileUpload(value);
                        setError(key as keyof { file: File }, {
                            type: 'manual',
                            message: value,
                        });
                    });
                }
            } catch (error) {
                handleError(error);
                dispatch(setIsLoading(false));
            } finally {
                dispatch(setIsLoading(false));
            }
        };
        uploadFileQuestionExcel();
    }, [file, dispatch, trigger, setError]);

    const handleDownloadFileError = async () => {
        try {
            const response = await downloadFileError();
            if (!response.success) {
                toast.error(response.message);
                return;
            }
            const link = document.createElement('a');
            link.href = response.data.file_url;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            handleError(error);
        }
    };
    const renderFileError = () => {
        if (errors.file) {
            return (
                <Box
                    sx={{
                        marginTop: '5px',
                        fontSize: '12px',
                        marginLeft: 0,
                        color: `${colors.textError} !important`,
                        fontWeight: '400',
                    }}
                >
                    {errors?.file?.message}
                </Box>
            );
        }
        return null;
    };
    return (
        <BaseDialog
            open={open}
            onClose={() => onClose()}
            title="Tải bảng câu hỏi bằng Excel"
            maxWidth="md"
            fullWidth
            sxContent={{ padding: 6 }}
        >
            <Grid container spacing={2} mt={2}>
                <Grid item xs={12}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            gap: 1,
                        }}
                    >
                        <Box sx={styles.guideText}>
                            <a href={urlGuideUpload}>
                                Hướng dẫn upload file mẫu
                            </a>
                        </Box>
                        <Button
                            href={urlExportQuestionTemplate}
                            variant="contained"
                            sx={styles.btnUpload}
                            startIcon={<IosShareOutlinedIcon />}
                        >
                            Xuất mẫu
                        </Button>
                    </Box>
                </Grid>
                <Grid item md={12}>
                    <FormProvider methods={methods} onSubmit={onSubmit}>
                        {!file ? (
                            <UploadFileArea
                                name="file"
                                sx={{
                                    width: '100%',
                                    height: '210px',
                                    border: `1px dashed ${colors.main}`,
                                    background: 'var(--color-primary-lighter)',
                                }}
                            />
                        ) : (
                            <>
                                <Box
                                    sx={{
                                        minHeight: '80px',
                                        background: `${(!uploadResponse?.success && uploadResponse !== undefined) || !errorFileUpload ? '#EF465C0D' : 'var(--color-primary-light-20)'}`,
                                        padding: '24px 35px',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        border: `1px solid ${(!uploadResponse?.success && uploadResponse !== undefined) || !errorFileUpload ? '#F52A37' : colors.main}`,
                                        borderRadius: '10px',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            color: common.black,
                                        }}
                                    >
                                        <Box>
                                            {(!uploadResponse?.success &&
                                                uploadResponse !== undefined) ||
                                            !errorFileUpload ? (
                                                <WarningAmberIcon
                                                    sx={{
                                                        color: 'warning.main',
                                                    }}
                                                />
                                            ) : (
                                                <InsertDriveFileOutlinedIcon
                                                    sx={{
                                                        color: 'var(--color-primary)',
                                                    }}
                                                />
                                            )}
                                        </Box>
                                        <Box>
                                            <Box
                                                sx={{
                                                    fontSize: '20px',
                                                    color: common.black,
                                                }}
                                            >
                                                {renderFileName()}
                                            </Box>
                                            <Box
                                                sx={{
                                                    fontSize: '12px',
                                                    color: `${
                                                        !uploadResponse?.success &&
                                                        uploadResponse
                                                            ? common.black
                                                            : '#13B75BB2'
                                                    }`,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                }}
                                            >
                                                {errorFileUpload}
                                                {!errorFileUpload &&
                                                    renderFileError()}
                                                <Typography
                                                    component={'a'}
                                                    sx={{
                                                        color: colors.main,
                                                        cursor: 'pointer',
                                                        fontSize: '12px',
                                                        textDecoration: 'none',
                                                    }}
                                                >
                                                    {!uploadResponse?.success &&
                                                    uploadResponse !==
                                                        undefined ? (
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems:
                                                                    'center',
                                                            }}
                                                            onClick={() =>
                                                                handleDownloadFileError()
                                                            }
                                                        >
                                                            Kiểm tra file lỗi
                                                            <FileDownloadOutlinedIcon />
                                                        </Box>
                                                    ) : (
                                                        ''
                                                    )}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                    <IconButton
                                        sx={{
                                            width: '33px',
                                            height: '33px',
                                            background: '#FF0000',
                                            '&:hover': {
                                                background: '#FF0000',
                                            },
                                        }}
                                        onClick={() => {
                                            resetField('file');
                                            setUploadResponse(undefined);
                                            setErrorFileUpload('');
                                        }}
                                    >
                                        <CloseIcon
                                            sx={{ color: common.white }}
                                        />
                                    </IconButton>
                                </Box>
                            </>
                        )}
                        {file && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    gap: 1,
                                    mt: 2,
                                }}
                            >
                                <Button
                                    type="submit"
                                    variant="outlined"
                                    sx={{
                                        ...styles.modalButton,
                                        borderWidth: '2px',
                                    }}
                                    onClick={() => {
                                        onClose();
                                        resetField('file');
                                        setUploadResponse(undefined);
                                        setErrorFileUpload('');
                                    }}
                                    startIcon={<CloseIcon />}
                                >
                                    Đóng
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    sx={styles.modalButton}
                                    disabled={!!errors.file}
                                    startIcon={<SaveIcon />}
                                >
                                    Lưu
                                </Button>
                            </Box>
                        )}
                    </FormProvider>
                </Grid>
            </Grid>
        </BaseDialog>
    );
};
const styles = createStyleSheet({
    modalButton: {
        textTransform: 'none',
        borderRadius: '60px',
        minHeight: '60px',
        minWidth: '155px',
        fontSize: '20px',
        fontWeight: '700',
    },
    guideText: {
        cursor: 'pointer',
        textDecoration: 'underline',
        color: colors.main,
        fontSize: '14px',
        fontWeight: '500',
    },
    btnUpload: {
        height: '53px',
        width: '175px',
        borderRadius: '60px',
        textTransform: 'none',
        fontSize: '16px',
        fontWeight: 700,
    },
});
export default ModalUplooadFileQuestion;
